import React, { useRef } from "react";
import { motion } from "motion/react";
import {
    ChevronLeft, Eye, Sparkles,
    Target, Search, Globe, Users, FileText, LayoutTemplate,
    Smartphone, Briefcase, ShoppingBag, Camera, Video,
    Film, Clapperboard, Megaphone, Palette, Code2, Mail,
    BarChart3, Fingerprint, Image as ImageIcon, Settings, PenTool, Mic,
    Headphones, Clock, BadgeCheck, Star, Flag, MessageCircle,
} from "lucide-react";

// Desktop "خدماتنا" mega-menu — 4 category columns (each backed only by
// slugs that actually exist in app/Support/ServiceCatalog.php, so every
// link resolves to a real page) + a featured CTA card, plus a 4-card trust
// strip along the bottom.
const SERVICE_CATEGORIES = [
    {
        key: "marketing",
        title: "التسويق الرقمي",
        icon: "Megaphone",
        viewAllHref: "/services",
        items: [
            { name: "خدمات اليوم الوطني السعودي 96", href: "/saudi-national-day-services", icon: "Flag" },
            { name: "إدارة حملات إعلانية", href: "/services/ادارة-حملات-اعلانية", icon: "Target" },
            { name: "تحسين محركات البحث (SEO)", href: "/services/شركة-سيو", icon: "Search" },
            { name: "إدارة وسائل التواصل الاجتماعي", href: "/services/ادارة-حسابات-التواصل-الاجتماعي", icon: "Users" },
            { name: "التسويق عبر المؤثرين", href: "/services/التسويق-عبر-المؤثرين", icon: "Star" },
            { name: "التسويق عبر البريد الإلكتروني", href: "/services/التسويق-عبر-البريد-الالكتروني", icon: "Mail" },
            { name: "تحليل البيانات وقياس الأداء", href: "/services/تحليل-البيانات-وقياس-الاداء", icon: "BarChart3" },
        ],
    },
    {
        key: "design",
        title: "التصميم والإبداع",
        icon: "Palette",
        viewAllHref: "/services",
        items: [
            { name: "تصميم الهوية البصرية", href: "/services/تصميم-الهوية-البصرية", icon: "Fingerprint" },
            { name: "تصميم بروفايل الشركات", href: "/services/تصميم-بروفايل-الشركات", icon: "Briefcase" },
            { name: "تصميم واجهات وتجربة المستخدم", href: "/services/تصميم-واجهات-وتجربة-المستخدم", icon: "LayoutTemplate" },
            { name: "تصميم الجرافيك", href: "/services/تصميم-جرافيك", icon: "ImageIcon" },
            { name: "تصميم الموشن جرافيك", href: "/services/تصميم-موشن-جرافيك", icon: "Film" },
        ],
    },
    {
        key: "development",
        title: "التطوير والبرمجة",
        icon: "Code2",
        viewAllHref: "/services",
        items: [
            { name: "أفضل شركة تصميم مواقع", href: "/services/أفضل-شركة-تصميم-مواقع", icon: "Globe" },
            { name: "تطوير تطبيقات الجوال", href: "/services/تصميم-تطبيقات", icon: "Smartphone" },
            { name: "تأسيس المتاجر الإلكترونية", href: "/services/تأسيس-متاجر", icon: "ShoppingBag" },
            { name: "إدارة وتطوير المتاجر الإلكترونية", href: "/services/ادارة-المتاجر-الالكترونية", icon: "Settings" },
            { name: "ربط واتساب API", href: "/ربط-واتساب-api", icon: "MessageCircle" },
        ],
    },
    {
        key: "content",
        title: "المحتوى والإنتاج",
        icon: "Video",
        viewAllHref: "/services",
        items: [
            { name: "كتابة المحتوى التسويقي", href: "/services/كتابة-المحتوى-التسويقي", icon: "PenTool" },
            { name: "إنتاج المحتوى الرقمي", href: "/services/انتاج-المحتوى-الرقمي", icon: "FileText" },
            { name: "إنتاج الفيديوهات الإعلانية", href: "/services/انتاج-الفيديوهات-الاعلانية", icon: "Video" },
            { name: "تصوير UGC احترافي", href: "/تصوير-ugc-سعودي", icon: "Clapperboard" },
            { name: "التعليق الصوتي", href: "/services/فويس-اوفر", icon: "Mic" },
            { name: "تصوير المنتجات التجارية", href: "/services/تصوير-المنتجات-التجارية", icon: "Camera" },
        ],
    },
];

const TRUST_BADGES = [
    { icon: "Users", title: "فريق متخصص", subtitle: "نخبة من الخبراء في كل مجال" },
    { icon: "BadgeCheck", title: "جودة مضمونة", subtitle: "معايير عالية في التنفيذ" },
    { icon: "Clock", title: "تسليم في الوقت", subtitle: "نلتزم بالمواعيد دائمًا" },
    { icon: "Headphones", title: "دعم مستمر", subtitle: "نرافقك في كل خطوة" },
];

// Exported so the mobile drawer accordion in Header.jsx renders the exact
// same category/link data instead of keeping a second copy in sync by hand.
export { SERVICE_CATEGORIES };

// Exported so SeoMegaMenu.jsx can reuse the same generic trust badges
// instead of duplicating them — the copy isn't service-specific.
export { TrustStrip };

// String → component lookup so the data above can stay plain serializable
// data instead of embedding component references directly.
export const MEGA_MENU_ICONS = {
    Target, Search, Globe, Users, FileText, LayoutTemplate,
    Smartphone, Briefcase, ShoppingBag, Camera, Video,
    Film, Clapperboard, Megaphone, Palette, Code2, Mail,
    BarChart3, Fingerprint, ImageIcon, Settings, PenTool, Mic,
    Headphones, Clock, BadgeCheck, Star, Flag, MessageCircle,
};

const columnVariants = {
    hidden: { opacity: 0, y: 8 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.28, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] },
    }),
};

// Every column shares the exact same anatomy — a fixed-height header, a
// list of fixed-height rows, and a footer link pinned via margin-top:auto —
// so columns with different item counts still line up header-to-header and
// footer-to-footer once the parent grid stretches them to equal height.
const CategoryColumn = ({ category, index, onNavigate }) => {
    const CategoryIcon = MEGA_MENU_ICONS[category.icon];
    return (
        <motion.div
            custom={index}
            variants={columnVariants}
            initial="hidden"
            animate="visible"
            className="flex h-full min-w-0 flex-col"
        >
            <div className="mb-[18px] flex h-[40px] shrink-0 items-center gap-2">
                <h4 className="text-[18px] font-bold leading-none text-[#0F172A]">
                    {category.title}
                </h4>
                {CategoryIcon && (
                    <CategoryIcon
                        className="h-[18px] w-[18px] shrink-0 text-primary-600"
                        aria-hidden="true"
                    />
                )}
            </div>

            <ul className="flex flex-1 flex-col gap-3" role="none">
                {category.items.map((item) => {
                    const ItemIcon = MEGA_MENU_ICONS[item.icon];
                    return (
                        <li key={item.href} className="shrink-0" role="none">
                            <a
                                href={item.href}
                                onClick={onNavigate}
                                role="menuitem"
                                className="group/tile flex h-[38px] items-center justify-between gap-3 rounded-lg px-2 text-[15px] font-medium leading-[1.4] text-[#334155] transition-[background-color,color] duration-[250ms] ease-out hover:bg-[#F8FAFC] hover:text-primary-600 focus-visible:bg-[#F8FAFC] focus-visible:text-primary-600 focus-visible:outline-none"
                            >
                                <span className="min-w-0 flex-1 truncate">
                                    {item.name}
                                </span>
                                {ItemIcon && (
                                    <ItemIcon
                                        className="h-4 w-4 shrink-0 text-[#94A3B8] transition-[color,transform] duration-[250ms] ease-out group-hover/tile:scale-110 group-hover/tile:text-primary-600"
                                        aria-hidden="true"
                                    />
                                )}
                            </a>
                        </li>
                    );
                })}
            </ul>

            <a
                href={category.viewAllHref}
                onClick={onNavigate}
                className="mt-auto inline-flex h-[40px] shrink-0 items-center gap-1.5 border-t border-[#E2E8F0] whitespace-nowrap text-[12.5px] font-bold text-primary-600 transition-colors hover:text-primary-700"
            >
                عرض جميع الخدمات
                <ChevronLeft className="h-3 w-3" aria-hidden="true" />
            </a>
        </motion.div>
    );
};

const FeaturedCtaCard = ({ onNavigate }) => (
    <motion.div
        custom={SERVICE_CATEGORIES.length}
        variants={columnVariants}
        initial="hidden"
        animate="visible"
        className="group/cta relative order-first flex w-full flex-col overflow-hidden rounded-[20px] border border-primary-100/60 p-8 text-right transition-shadow duration-300 lg:order-none lg:w-[320px] lg:shrink-0"
        style={{
            background: "linear-gradient(135deg, #F8FAFF, #EEF4FF)",
            boxShadow: "0 20px 60px rgba(37,99,235,0.12)",
        }}
    >
        {/* Floating decorative circles — abstract background */}
        <span
            className="pointer-events-none absolute -top-12 -left-12 h-40 w-40 rounded-full bg-primary-200/30 blur-3xl transition-transform duration-700 group-hover/cta:scale-110"
            aria-hidden="true"
        />
        <span
            className="pointer-events-none absolute bottom-6 left-6 h-16 w-16 rounded-full bg-primary-300/20 blur-2xl"
            aria-hidden="true"
        />
        <span
            className="pointer-events-none absolute top-1/3 -right-6 h-24 w-24 rounded-full border border-primary-200/50"
            aria-hidden="true"
        />

        <span className="relative mb-5 inline-flex w-fit items-center gap-1.5 rounded-full bg-white/80 px-3.5 py-1.5 text-[11px] font-bold text-primary-600">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            حلول رقمية متكاملة
        </span>

        <h4 className="relative mb-2.5 text-[20px] font-bold leading-snug text-[#0F172A]">
            نصمم وننمو علامتك التجارية رقميًا
        </h4>
        <p className="relative mb-7 text-[13.5px] font-medium leading-relaxed text-[#64748B]">
            من الفكرة إلى التنفيذ، نقدم لك كل ما تحتاجه لتبني حضور رقمي قوي
            ومؤثر.
        </p>

        <div className="relative mt-auto flex w-full flex-col gap-2.5">
            <a
                href="/services"
                onClick={onNavigate}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-primary-600 px-4 py-3 text-[13.5px] font-bold text-white shadow-[0_10px_24px_rgba(37,99,235,0.25)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-primary-700 hover:shadow-[0_14px_30px_rgba(37,99,235,0.32)]"
            >
                استكشف خدماتنا
                <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
            <a
                href="/portfolio"
                onClick={onNavigate}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-primary-200 bg-white/60 px-4 py-2.5 text-[13.5px] font-bold text-primary-600 transition-colors duration-300 hover:bg-white"
            >
                <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                شاهد أعمالنا
            </a>
        </div>
    </motion.div>
);

const TrustStrip = () => (
    <div className="mt-10 grid grid-cols-2 gap-4 border-t border-[#E2E8F0] pt-8 lg:grid-cols-4">
        {TRUST_BADGES.map((badge) => {
            const BadgeIcon = MEGA_MENU_ICONS[badge.icon];
            return (
                <div
                    key={badge.title}
                    className="group/feat flex h-[72px] items-center justify-start gap-3 rounded-2xl border border-transparent bg-[#F8FAFC] px-4 text-right transition-all duration-300 hover:-translate-y-0.5 hover:border-primary-200 hover:bg-white hover:shadow-[0_12px_28px_rgba(15,23,42,0.08)]"
                >
                    <div className="min-w-0">
                        <p className="truncate text-[13px] font-bold text-[#0F172A]">
                            {badge.title}
                        </p>
                        <p className="truncate text-[11.5px] font-medium text-[#64748B]">
                            {badge.subtitle}
                        </p>
                    </div>
                    {BadgeIcon && (
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600 transition-transform duration-300 group-hover/feat:scale-110">
                            <BadgeIcon className="h-4.5 w-4.5" aria-hidden="true" />
                        </span>
                    )}
                </div>
            );
        })}
    </div>
);

// `onClose` fires on link click (mouse) and on Escape/outside-click, wired
// by the parent Header. `menuRef` is exposed so the parent's keyboard
// handler can move focus between menuitems with arrow keys.
const ServicesMegaMenu = React.forwardRef(({ onClose }, ref) => {
    const fallbackRef = useRef(null);
    const panelRef = ref || fallbackRef;

    return (
        <motion.div
            ref={panelRef}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            role="menu"
            aria-label="خدماتنا"
            className="fixed left-1/2 top-[92px] z-40 max-h-[80vh] w-[min(94vw,1440px)] -translate-x-1/2 overflow-y-auto rounded-[24px] bg-white"
            style={{ boxShadow: "0 40px 120px rgba(15,23,42,0.12)" }}
        >
            <div className="px-8 py-10 sm:px-10 lg:px-12">
                {/* DOM order is [categories..., CTA] so under dir="rtl" the
                    first column lands on the right and the CTA lands on the
                    far left, matching the reference layout. */}
                <div className="flex flex-col-reverse gap-10 text-right lg:flex-row lg:items-stretch lg:gap-10">
                    <div className="grid flex-1 grid-cols-2 gap-x-8 gap-y-10 lg:grid-cols-4 lg:gap-x-10">
                        {SERVICE_CATEGORIES.map((category, i) => (
                            <CategoryColumn
                                key={category.key}
                                category={category}
                                index={i}
                                onNavigate={onClose}
                            />
                        ))}
                    </div>
                    <FeaturedCtaCard onNavigate={onClose} />
                </div>

                <TrustStrip />
            </div>
        </motion.div>
    );
});

ServicesMegaMenu.displayName = "ServicesMegaMenu";

export default ServicesMegaMenu;
